import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { BRAND_VIDEO } from "@/lib/video";

/**
 * Local poster used on teaser (non-watch) pages. Keeping YouTube thumbnails and
 * embeds off these pages means Google only sees one watch page (/fleet#video),
 * instead of flagging every city page with "video isn't on a watch page".
 */
const TEASER_POSTER = "/images/city-fleet-chauffeur.jpg";

type Props = {
  heading?: string;
  blurb?: string;
  /** Unique accessible label so anchor-text validation stays clean per page. */
  playLabel: string;
  /** Page-specific alt text for the poster frame. */
  posterAlt?: string;
  className?: string;
  /**
   * Watch-page mode: renders the real player immediately (no click facade) so
   * the video is the prominent main content Google requires for video indexing.
   */
  prominent?: boolean;
};

/**
 * Click-to-load YouTube facade. Renders only a poster image and a button
 * until the visitor asks for the video, so no third-party player script
 * touches LCP or the main thread on first load.
 */
export function VideoShowcase({
  heading = "See the fleet and the standard",
  blurb,
  playLabel,
  posterAlt,
  className = "",
  prominent = false,
}: Props) {
  const [playing, setPlaying] = useState(prominent);

  return (
    <section id={prominent ? "video" : undefined} className={`border-y border-border bg-surface ${className}`}>
      <div className="container-x py-16 md:py-20 grid gap-10 lg:grid-cols-[1fr_1.25fr] items-center">
        <div>
          <p className="eyebrow mb-3">Watch</p>
          <h2 className="text-3xl md:text-4xl mb-4">{heading}</h2>
          {blurb ? <p className="text-muted-foreground leading-relaxed">{blurb}</p> : null}
        </div>

        <div className="relative aspect-video overflow-hidden border border-border bg-surface-2">
          {!prominent ? (
            <Link
              to="/fleet"
              hash="video"
              aria-label={playLabel}
              className="group absolute inset-0 block h-full w-full"
            >
              <img
                src={TEASER_POSTER}
                alt={posterAlt ?? "Premier Executive Chauffeur black sedan, Suburban SUV and Mercedes-Benz Sprinter with uniformed chauffeurs"}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <span className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/20" />
              <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold bg-black/60">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="ml-1 h-7 w-7 fill-gold">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </Link>
          ) : playing ? (
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`${BRAND_VIDEO.embedUrl}?autoplay=1&rel=0&modestbranding=1`}
              title={playLabel}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              loading="eager"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label={playLabel}
              className="group absolute inset-0 h-full w-full cursor-pointer"
            >
              <img
                src={BRAND_VIDEO.thumbnail}
                alt={posterAlt ?? "Premier Executive Chauffeur fleet video"}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
